import { memo, useMemo, useState } from 'react';
import { useGetOrders } from '../../hooks/orders/queries';
import { IOrder } from '../../interfaces/Order';
import { Grid, Text } from '@mantine/core';
import moment from 'moment';
import momentTz from 'moment-timezone';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState
} from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import { useGetPaymentLinesReportData } from '../../hooks/reports/queries';
import { formatMoneyString } from '../../utils/strings';
import { DatePickerInput } from '@mantine/dates';
import { buildOrderFolio } from '../../utils/ordersUtils';

export default memo(function PaymentLinesByDateReport({
  date = '',
  close = () => {},
  storeId = ''
}: {
  date: string;
  close: () => void;
  storeId: string;
}) {
  const { t } = useTranslation(['FieldName']);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const [currentDate, setCurrentDate] = useState(date);

  const { data, isLoading, isError } = useGetPaymentLinesReportData(storeId, {
    fromDate: moment(currentDate).startOf('day').toISOString(),
    toDate: moment(currentDate).endOf('day').toISOString(),
    timezone: momentTz.tz.guess(true),
    pageNum: pagination.pageIndex,
    limit: pagination.pageSize
  });

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  const tableInfo = useMemo(() => {
    return data?.pages?.reduce((list: any[], { graphInfo }: { graphInfo: any[] }) => {
      list.push(...graphInfo);
      return list;
    }, [] as any[]);
  }, [data]);

  const footerInfo = useMemo(() => {
    return {
      totalAmountPaid: data?.pages[0]?.totalAmountPaid || 0,
      totalFeeTotal: data?.pages[0]?.totalFeeTotal || 0,
      totalNetAmount: data?.pages[0]?.totalNetAmount || 0,
    };
  }, [data?.pages]);

  const columns = [
    {
      accessorFn: (row: IOrder) => buildOrderFolio(row?.folioPrefix, row?.sequence),
      header: t('FieldName:order')
    },
    {
      accessorFn: (row) => t(`FieldName:paymentMethods.${row.paymentLines.paymentMethod}`),
      header: t('FieldName:paymentMethod')
    },
    {
      accessorFn: (row) => formatMoneyString((row.paymentLines.amountPaid || 0).toFixed(2)),
      header: t('FieldName:amount'),
      Footer: () => {
        return (
          <Grid>
            <Grid.Col span={12}>
              <Text fw={'bold'}>{formatMoneyString(footerInfo?.totalAmountPaid.toFixed(2))}</Text>
            </Grid.Col>
          </Grid>
        );
      }
    },
    {
      accessorFn: (row) => moment(row.paymentLines.paymentDate).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:date')
    },
    {
      accessorFn: (row) => row.paymentLines?.cashierBox?.name,
      header: t('FieldName:cashierBox')
    },
    {
      accessorFn: (row) => row.paymentLines?.createdBy?.name,
      header: t('FieldName:createdBy')
    },
    {
      accessorFn: (row) =>
        row.paymentLines.isOnACashUpReport ? t('FieldName:yes') : t('FieldName:no'),
      header: t('FieldName:isOnACashUpReport')
    }
  ];

  const table = useMantineReactTable({
    columns,
    data: (tableInfo || []) as any[], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: 'Error cargando información'
        }
      : undefined,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: true,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualSorting: true,
    mantinePaginationProps: {
      showRowsPerPage: false
    },
    paginationDisplayMode: 'pages',
    state: { pagination, isLoading: isLoading }, //pass the pagination state to the table
    manualPagination: true,
    pageCount: pages,
    rowCount: totalRowsCount,
    onPaginationChange: setPagination,
    mantineTableProps: {
      highlightOnHover: false,
      striped: 'odd',
      withColumnBorders: true,
      withRowBorders: true,
      withTableBorder: true
    }
  });

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <DatePickerInput
            value={new Date(currentDate)}
            onChange={(value) => setCurrentDate(value?.toISOString() || '')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <MantineReactTable table={table} />
        </Grid.Col>
      </Grid>
    </>
  );
});
